import { useState, useEffect } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { CalendarCheck, Clock, CheckCircle, XCircle, Star, Send, Video, Heart } from "lucide-react";
import { CalendarSyncButton } from "@/components/CalendarSyncDialog";
import { getMeetingLink } from "@/lib/sessionUtils";
import CoachReviewDialog from "@/components/CoachReviewDialog";

const STATUS_CONFIG = {
  scheduled:  { label: "Prévue",    icon: Clock,       cls: "bg-blue-50 text-blue-600 border-blue-200" },
  completed:  { label: "Terminée",  icon: CheckCircle, cls: "bg-green-50 text-green-600 border-green-200" },
  cancelled:  { label: "Annulée",   icon: XCircle,     cls: "bg-red-50 text-red-600 border-red-200" },
};

const TABS = [
  { key: "all", label: "Toutes" },
  { key: "upcoming", label: "À venir" },
  { key: "completed", label: "Terminées" },
];

function StarRating({ value, onChange, readonly = false }) {
  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map(i => (
        <button
          key={i}
          type="button"
          disabled={readonly}
          onClick={() => onChange?.(i)}
          className={`transition-colors ${readonly ? 'cursor-default' : 'cursor-pointer hover:scale-110'}`}
        >
          <Star className={`w-6 h-6 ${i <= value ? 'fill-yellow-400 text-yellow-400' : 'text-slate-200'}`} />
        </button>
      ))}
    </div>
  );
}

function formatDate(date, time) {
  if (!date) return "";
  try {
    const d = new Date(`${date}T${time || "00:00"}`);
    return d.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" }) + (time ? ` à ${time.slice(0, 5)}` : "");
  } catch { return `${date}${time ? " à " + time : ""}`; }
}

function isPast(date) {
  const today = new Date().toISOString().split("T")[0];
  return date <= today;
}

export default function StudentSessions() {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("all");
  const [checkinSession, setCheckinSession] = useState(null);
  const [rating, setRating] = useState(0);
  const [feedback, setFeedback] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);

  const fetchSessions = () => {
    api.get("/coaching-sessions")
      .then(r => setSessions(r.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchSessions(); }, []);

  const filtered = sessions.filter(s => {
    if (tab === "upcoming") return s.status === "scheduled";
    if (tab === "completed") return s.status === "completed";
    return true;
  }).sort((a, b) => b.date.localeCompare(a.date));

  const handleCheckin = async () => {
    if (!checkinSession) return;
    setSubmitting(true);
    try {
      await api.patch(`/coaching-sessions/${checkinSession.session_id}/checkin`, {
        rating: rating || null,
        feedback: feedback.trim() || null,
      });
      toast.success("Check-in enregistré !");
      setCheckinSession(null);
      setRating(0);
      setFeedback("");
      fetchSessions();
      setReviewOpen(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally { setSubmitting(false); }
  };

  return (
    <div>
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-2xl bg-purple-100 flex items-center justify-center flex-shrink-0 mt-0.5">
            <CalendarCheck className="w-6 h-6 text-purple-600" />
          </div>
          <div>
            <h1 className="font-heading text-3xl font-bold tracking-tight uppercase text-slate-900">Mes Séances</h1>
            <p className="text-slate-400 mt-1 text-sm">Consultez vos séances et faites votre check-in</p>
          </div>
        </div>
        <div className="flex gap-2 mt-1">
          <Button variant="outline" size="sm" onClick={() => setReviewOpen(true)} className="gap-1.5 rounded-full">
            <Heart className="w-4 h-4 text-blue-600" /> Avis coach
          </Button>
          <CalendarSyncButton accent="purple" />
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mt-6">
        {TABS.map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${tab === t.key ? 'bg-purple-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Sessions list */}
      <div className="space-y-3 mt-6">
        {loading ? (
          Array.from({ length: 3 }).map((_, i) => (
            <Card key={i} className="border-slate-100"><CardContent className="p-4"><div className="flex gap-3"><Skeleton className="w-11 h-11 rounded-xl" /><div className="flex-1 space-y-2"><Skeleton className="h-4 w-40" /><Skeleton className="h-3 w-56" /></div></div></CardContent></Card>
          ))
        ) : filtered.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-3">
              <CalendarCheck className="w-5 h-5 text-slate-400" />
            </div>
            <p className="text-sm text-slate-400">Aucune séance trouvée</p>
          </div>
        ) : filtered.map(s => {
          const cfg = STATUS_CONFIG[s.status] || STATUS_CONFIG.scheduled;
          const Icon = cfg.icon;
          const canCheckin = s.status === "scheduled" && isPast(s.date) && !s.completed_by_student;
          const meetingLink = getMeetingLink(s);

          return (
            <Card key={s.session_id} className="border-slate-100 hover:shadow-sm transition-shadow">
              <CardContent className="p-4">
                <div className="flex items-center gap-4">
                  <div className="w-11 h-11 rounded-xl bg-purple-100 flex items-center justify-center flex-shrink-0">
                    <CalendarCheck className="w-5 h-5 text-purple-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-semibold text-slate-900">{s.title}</span>
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold border ${cfg.cls}`}>
                        <Icon className="w-3 h-3" />{cfg.label}
                      </span>
                    </div>
                    <p className="text-xs text-slate-400 mt-0.5">{formatDate(s.date, s.time)} · {s.duration} min</p>
                    {s.notes && <p className="text-xs text-slate-500 mt-1">{s.notes}</p>}
                    {meetingLink && (
                      <a href={meetingLink} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-purple-600 hover:underline mt-1.5 font-medium">
                        <Video className="w-3.5 h-3.5" /> Rejoindre la visio
                      </a>
                    )}
                  </div>
                  {canCheckin && (
                    <Button
                      size="sm"
                      className="bg-purple-600 hover:bg-purple-700 text-white rounded-full text-xs"
                      onClick={() => { setCheckinSession(s); setRating(0); setFeedback(""); }}
                    >
                      <CheckCircle className="w-3.5 h-3.5 mr-1" /> Check-in
                    </Button>
                  )}
                </div>
                {/* Show feedback if completed by student */}
                {s.completed_by_student && (s.student_rating || s.student_feedback) && (
                  <div className="mt-3 ml-15 pl-4 border-l-2 border-green-200">
                    {s.student_rating && <StarRating value={s.student_rating} readonly />}
                    {s.student_feedback && <p className="text-xs text-slate-500 mt-1 italic">"{s.student_feedback}"</p>}
                    {s.completed_at && <p className="text-xs text-slate-300 mt-1">Complété le {new Date(s.completed_at).toLocaleDateString("fr-FR")}</p>}
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Checkin Dialog */}
      <Dialog open={!!checkinSession} onOpenChange={v => !v && setCheckinSession(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading text-xl">Check-in : {checkinSession?.title}</DialogTitle></DialogHeader>
          <div className="space-y-5 mt-4">
            <div>
              <p className="text-sm font-medium text-slate-700 mb-2">Comment s'est passée la séance ?</p>
              <StarRating value={rating} onChange={setRating} />
            </div>
            <div>
              <p className="text-sm font-medium text-slate-700 mb-2">Votre ressenti (optionnel)</p>
              <Textarea
                value={feedback}
                onChange={e => setFeedback(e.target.value)}
                placeholder="Difficulté, sensations, notes..."
                rows={3}
                className="resize-none"
              />
            </div>
            <Button
              onClick={handleCheckin}
              disabled={submitting}
              className="w-full bg-purple-600 hover:bg-purple-700 text-white rounded-full h-11"
            >
              {submitting ? "..." : <><Send className="w-4 h-4 mr-2" /> Valider le check-in</>}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <CoachReviewDialog open={reviewOpen} onOpenChange={setReviewOpen} />
    </div>
  );
}
